/**
 * Client-side filtering for grouped MMY vehicle collection pages.
 *
 * These pages render the same kit once per (height × position) it fits, driven by collection
 * metafields — so Shopify Search & Discovery (which filters the unique product set and reloads)
 * can't express them. Instead the sidebar (snippets/mmy-collection-filters.liquid) renders
 * checkbox facets wired here purely by input name (mmy-facet-<axis>), and we show/hide the
 * pre-rendered occurrences in place. Height is single-select (one vehicle height at a time);
 * position, fitment time, and availability stay multi-select. Selections are mirrored to the
 * URL so filtered views are shareable and the MMY vehicle search can deep-link into a grid.
 */
(function mmyCollectionFilter() {
  const MMY_STORAGE_KEY = 'mmy';
  const MMY_HEIGHT_COOKIE = 'mmy_height';
  const COUNT_PLACEHOLDER = '[[COUNT]]';

  // URL params we own. height/position/fittime are repeated params (one entry per value);
  // stock is '1' when "in stock" is active.
  const PARAM = { height: 'height', position: 'position', fittime: 'fit', stock: 'stock' };
  const FACET_PREFIX = 'mmy-facet-';

  let applying = false;
  let scheduled = false;
  let observer = null;

  /**
   * Active filter. Height is single-select. Position/fittime are multi-select (OR within an
   * axis, AND across axes). inStock is a boolean.
   * @type {{ height: Set<string>, position: Set<string>, fittime: Set<string>, inStock: boolean }}
   */
  const state = {
    height: new Set(),
    position: new Set(),
    fittime: new Set(),
    inStock: false,
  };

  /** @type {WeakMap<Element, string>} */
  const originalCountText = new WeakMap();

  /**
   * @param {string} label
   * @returns {string}
   */
  function heightLabelToSlug(label) {
    return String(label || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/#/g, '')
      .replace(/"/g, '');
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  function readCookie(name) {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
    );
    const value = match?.[1];
    return value ? decodeURIComponent(value) : '';
  }

  /**
   * @returns {{ height?: string, translations?: { itemCount?: { one: string, other: string }, seeItems?: { one: string, other: string } } } | null}
   */
  function readMmyFilterConfig() {
    const config = document.getElementById('mmy-filter-config');
    if (!config?.textContent) return null;

    try {
      return JSON.parse(config.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * The vehicle's height, seeded from the MMY search (cookie / localStorage / config).
   * @returns {string}
   */
  function readMmyHeightFilter() {
    try {
      const localState = JSON.parse(localStorage.getItem(MMY_STORAGE_KEY) || '{}');
      const selheights = String(localState.selheights || '').trim();
      if (selheights) {
        return heightLabelToSlug(selheights);
      }
    } catch (error) {
      // Ignore parse errors.
    }

    const cookieHeight = readCookie(MMY_HEIGHT_COOKIE);
    if (cookieHeight) return heightLabelToSlug(cookieHeight);

    const grid = document.querySelector('[data-mmy-collection-grid]');
    if (grid instanceof HTMLElement && grid.dataset.mmyFilterHeight) {
      return heightLabelToSlug(grid.dataset.mmyFilterHeight);
    }

    const config = readMmyFilterConfig();
    if (config?.height) return heightLabelToSlug(config.height);

    return '';
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getCollectionGrid() {
    const grid = document.querySelector('[data-mmy-collection-grid]');
    return grid instanceof HTMLElement ? grid : null;
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement}
   */
  function getCollectionRoot(grid) {
    return grid.closest('results-list') || grid;
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  function getProductGridItems(grid) {
    return [...grid.querySelectorAll('li.product-grid__item[data-mmy-height]')].filter(
      (node) => node instanceof HTMLElement,
    );
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLInputElement[]}
   */
  function getFacetInputs(root) {
    return [...root.querySelectorAll(`input[name^="${FACET_PREFIX}"]`)].filter(
      (node) => node instanceof HTMLInputElement,
    );
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string}
   */
  function getFacetAxis(input) {
    return input.name.slice(FACET_PREFIX.length);
  }

  /**
   * A vehicle has one height. Drop extras from shared links / stale checkbox copies.
   */
  function keepSingleHeight() {
    if (state.height.size <= 1) return;

    const [first] = state.height;
    state.height.clear();
    if (first) state.height.add(first);
  }

  /**
   * Uncheck every other height input in the same form copy.
   * @param {HTMLInputElement[]} inputs
   * @param {HTMLInputElement} selected
   */
  function exclusiveCheckHeight(inputs, selected) {
    for (const input of inputs) {
      if (input === selected) continue;
      if (getFacetAxis(input) !== 'height') continue;
      input.checked = false;
    }
  }

  /**
   * @returns {boolean}
   */
  function isFiltered() {
    return (
      state.height.size > 0 || state.position.size > 0 || state.fittime.size > 0 || state.inStock
    );
  }

  /* ───────────────── State ⇄ URL ───────────────── */

  /**
   * Seed state from the URL. Returns true if any of our params were present.
   * @returns {boolean}
   */
  function readStateFromUrl() {
    const params = new URL(window.location.href).searchParams;
    let any = false;

    const load = (param, set, asSlug) => {
      if (!params.has(param)) return;
      any = true;
      set.clear();
      for (const raw of params.getAll(param)) {
        const value = asSlug ? heightLabelToSlug(raw) : String(raw).trim();
        if (value) set.add(value);
      }
    };

    load(PARAM.height, state.height, true);
    keepSingleHeight();
    load(PARAM.position, state.position, true);
    load(PARAM.fittime, state.fittime, false);

    if (params.has(PARAM.stock)) {
      any = true;
      state.inStock = params.get(PARAM.stock) === '1';
    }

    return any;
  }

  /**
   * Reflect the current state back into the URL (replaceState, no history entry).
   */
  function writeStateToUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    for (const param of Object.values(PARAM)) {
      params.delete(param);
    }
    for (const value of state.height) params.append(PARAM.height, value);
    for (const value of state.position) params.append(PARAM.position, value);
    for (const value of state.fittime) params.append(PARAM.fittime, value);
    if (state.inStock) params.set(PARAM.stock, '1');

    window.history.replaceState(null, '', url.toString());
  }

  /**
   * Drop any seeded values that aren't actually offered on this page (e.g. a shared link from a
   * different vehicle), so the URL and the checkboxes stay in sync with what's filterable.
   * @param {HTMLElement} root
   */
  function pruneStateToOptions(root) {
    const options = { height: new Set(), position: new Set(), fittime: new Set() };

    for (const input of getFacetInputs(root)) {
      const axis = getFacetAxis(input);
      if (axis === 'height') options.height.add(heightLabelToSlug(input.value));
      else if (axis === 'position') options.position.add(heightLabelToSlug(input.value));
      else if (axis === 'fittime') options.fittime.add(input.value.trim());
    }

    for (const axis of ['height', 'position', 'fittime']) {
      for (const value of [...state[axis]]) {
        if (!options[axis].has(value)) state[axis].delete(value);
      }
    }
  }

  /* ───────────────── State ⇄ checkboxes ───────────────── */

  /**
   * @param {HTMLElement} root
   */
  function readStateFromInputs(root) {
    state.height.clear();
    state.position.clear();
    state.fittime.clear();
    state.inStock = false;

    for (const input of getFacetInputs(root)) {
      if (!input.checked) continue;
      const axis = getFacetAxis(input);
      if (axis === 'height') {
        // Last checked height wins if a stale copy still has two selected.
        state.height.clear();
        state.height.add(heightLabelToSlug(input.value));
      } else if (axis === 'position') {
        state.position.add(heightLabelToSlug(input.value));
      } else if (axis === 'fittime') {
        state.fittime.add(input.value.trim());
      } else if (axis === 'stock') {
        state.inStock = true;
      }
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function reflectStateToInputs(root) {
    for (const input of getFacetInputs(root)) {
      const axis = getFacetAxis(input);
      let checked = false;
      if (axis === 'height') checked = state.height.has(heightLabelToSlug(input.value));
      else if (axis === 'position') checked = state.position.has(heightLabelToSlug(input.value));
      else if (axis === 'fittime') checked = state.fittime.has(input.value.trim());
      else if (axis === 'stock') checked = state.inStock;
      input.checked = checked;
    }
  }

  /* ───────────────── Filtering ───────────────── */

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  function itemMatches(item) {
    const height = heightLabelToSlug(item.getAttribute('data-mmy-height') || '');
    const position = heightLabelToSlug(item.getAttribute('data-mmy-position') || '');
    const fittime = (item.getAttribute('data-mmy-fittime') || '').trim();
    const available = (item.getAttribute('data-mmy-available') || '') === 'true';

    const heightMatch =
      state.height.size === 0 || state.height.has(height) || height === 'any_height';
    const positionMatch = state.position.size === 0 || state.position.has(position);
    const fittimeMatch = state.fittime.size === 0 || state.fittime.has(fittime);
    const availableMatch = !state.inStock || available;

    return heightMatch && positionMatch && fittimeMatch && availableMatch;
  }

  /**
   * @param {HTMLElement} grid
   */
  function sortMmyCollectionGrid(grid) {
    const productGrid = grid.querySelector('.product-grid');
    if (!(productGrid instanceof HTMLElement)) return;

    const items = [...productGrid.querySelectorAll('li.product-grid__item[data-mmy-sort-key]')].filter(
      (node) => node instanceof HTMLElement,
    );

    if (items.length < 2) return;

    const sortedItems = items.slice().sort((left, right) => {
      const leftKey = left.getAttribute('data-mmy-sort-key') || '';
      const rightKey = right.getAttribute('data-mmy-sort-key') || '';
      return leftKey.localeCompare(rightKey);
    });

    const needsReorder = sortedItems.some((item, index) => item !== items[index]);
    if (!needsReorder) return;

    for (const item of sortedItems) {
      productGrid.appendChild(item);
    }
  }

  /**
   * @param {HTMLElement} grid
   * @returns {number}
   */
  function countVisibleProducts(grid) {
    return [...grid.querySelectorAll('li.product-grid__item')].filter(
      (item) => item instanceof HTMLElement && !isProductItemHidden(item),
    ).length;
  }

  /**
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  function isProductItemHidden(item) {
    return item.classList.contains('mmy-filter-hidden') || item.hidden;
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} visible
   */
  function setProductItemVisible(item, visible) {
    if (visible === !isProductItemHidden(item)) {
      return;
    }

    if (visible) {
      item.hidden = false;
      item.classList.remove('mmy-filter-hidden');
      item.style.removeProperty('display');
      item.removeAttribute('aria-hidden');
      return;
    }

    item.hidden = true;
    item.classList.add('mmy-filter-hidden');
    item.style.display = 'none';
    item.setAttribute('aria-hidden', 'true');
  }

  /**
   * @param {Element} element
   */
  function rememberOriginalCountText(element) {
    if (originalCountText.has(element)) return;
    const text = element.textContent?.trim();
    if (text) originalCountText.set(element, text);
  }

  /**
   * @param {HTMLElement} grid
   */
  function cacheOriginalProductsCountTexts(grid) {
    const root = getCollectionRoot(grid);

    for (const span of root.querySelectorAll('.products-count-wrapper span')) {
      rememberOriginalCountText(span);
    }

    for (const button of root.querySelectorAll('.facets__see-results, [data-mmy-see-results]')) {
      rememberOriginalCountText(button);
    }
  }

  /**
   * @param {number} count
   * @param {{ one?: string, other?: string } | undefined} templates
   * @returns {string}
   */
  function formatCountLabel(count, templates) {
    if (!templates?.one || !templates?.other) return '';

    const template = count === 1 ? templates.one : templates.other;
    return template.split(COUNT_PLACEHOLDER).join(String(count));
  }

  /**
   * @param {HTMLElement} grid
   * @param {boolean} isActive
   * @param {number} visibleCount
   */
  function updateProductsCountDisplay(grid, isActive, visibleCount) {
    const root = getCollectionRoot(grid);
    const config = readMmyFilterConfig();
    const countSpans = root.querySelectorAll('.products-count-wrapper span');
    const seeResultsButtons = root.querySelectorAll('.facets__see-results, [data-mmy-see-results]');

    for (const element of [...countSpans, ...seeResultsButtons]) {
      rememberOriginalCountText(element);
    }

    const seeItemsText = formatCountLabel(visibleCount, config?.translations?.seeItems);

    if (!isActive) {
      for (const span of countSpans) {
        const original = originalCountText.get(span);
        if (original) span.textContent = original;
      }
    } else {
      const itemCountText = formatCountLabel(visibleCount, config?.translations?.itemCount);
      if (itemCountText) {
        for (const span of countSpans) {
          span.textContent = itemCountText;
        }
      }
    }

    // Drawer "See results" always reflects the currently visible count.
    if (seeItemsText) {
      for (const button of seeResultsButtons) {
        button.textContent = seeItemsText;
      }
    }
  }

  /**
   * @returns {number}
   */
  function activeFilterCount() {
    return state.height.size + state.position.size + state.fittime.size + (state.inStock ? 1 : 0);
  }

  /**
   * Update the per-facet count badges, clear buttons, and drawer/toggle bubbles.
   * @param {HTMLElement} grid
   */
  function updateFacetsUi(grid) {
    const root = getCollectionRoot(grid);
    const filtered = isFiltered();
    const totalActive = activeFilterCount();

    const counts = {
      height: state.height.size,
      position: state.position.size,
      fittime: state.fittime.size,
      stock: state.inStock ? 1 : 0,
    };

    for (const span of root.querySelectorAll('[data-mmy-facet-status]')) {
      const axis = span.getAttribute('data-mmy-facet-status') || '';
      const count = counts[axis] || 0;
      span.textContent = count > 0 ? String(count) : '';
      span.classList.toggle('bubble', count > 0);
      span.classList.toggle('facets__bubble', count > 0);
    }

    for (const clearButton of root.querySelectorAll('[data-mmy-facet-clear]')) {
      if (!(clearButton instanceof HTMLElement)) continue;

      // Drawer clear uses the native .facets__clear-all / --active display pattern.
      // Sidebar clear is a text link that uses the hidden attribute.
      if (clearButton.hasAttribute('data-mmy-facet-clear-drawer')) {
        clearButton.classList.toggle('facets__clear-all--active', filtered);
        clearButton.removeAttribute('hidden');
      } else {
        clearButton.hidden = !filtered;
        clearButton.classList.remove('facets__clear-all--active');
      }
    }

    for (const bubble of root.querySelectorAll('[data-mmy-filter-count-bubble]')) {
      if (!(bubble instanceof HTMLElement)) continue;
      bubble.hidden = totalActive === 0;
      const text = bubble.querySelector('[data-mmy-filter-count-text]');
      if (text) text.textContent = String(totalActive);
    }

    for (const bubble of root.querySelectorAll('[data-mmy-drawer-bubble]')) {
      if (!(bubble instanceof HTMLElement)) continue;
      bubble.hidden = totalActive === 0;
      bubble.textContent = totalActive > 0 ? String(totalActive) : '';
    }
  }

  /**
   * Update the sidebar/drawer "N results" counts.
   * @param {HTMLElement} grid
   * @param {number} count
   */
  function updateResultCount(grid, count) {
    const config = readMmyFilterConfig();
    const text =
      formatCountLabel(count, config?.translations?.itemCount) || String(count);

    for (const element of getCollectionRoot(grid).querySelectorAll('[data-mmy-facet-count]')) {
      if (element instanceof HTMLElement) {
        element.textContent = text;
      }
    }
  }

  /**
   * @returns {number}
   */
  function applyMmyCollectionFilter() {
    if (applying) return 0;

    const grid = getCollectionGrid();
    if (!grid) return 0;

    applying = true;

    try {
      const items = getProductGridItems(grid);
      const filtered = isFiltered();

      updateFacetsUi(grid);

      if (!filtered) {
        for (const item of items) {
          setProductItemVisible(item, true);
        }

        sortMmyCollectionGrid(grid);
        const totalCount = countVisibleProducts(grid);
        updateProductsCountDisplay(grid, false, totalCount);
        updateResultCount(grid, totalCount);
        return totalCount;
      }

      for (const item of items) {
        setProductItemVisible(item, itemMatches(item));
      }

      const visibleCount = countVisibleProducts(grid);

      if (grid.dataset.mmyVisibleCount !== String(visibleCount)) {
        grid.dataset.mmyVisibleCount = String(visibleCount);
      }

      updateProductsCountDisplay(grid, true, visibleCount);
      updateResultCount(grid, visibleCount);
      sortMmyCollectionGrid(grid);

      return visibleCount;
    } finally {
      applying = false;
    }
  }

  function scheduleMmyCollectionFilter() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      applyMmyCollectionFilter();
    });
  }

  /**
   * Clear all facet selections and refresh the grid + URL.
   * @param {HTMLElement} root
   */
  function clearAllFacets(root) {
    state.height.clear();
    state.position.clear();
    state.fittime.clear();
    state.inStock = false;
    reflectStateToInputs(root);
    writeStateToUrl();
    scheduleMmyCollectionFilter();
  }

  /**
   * Wire sidebar + drawer checkbox facets to the filter state, seed from the URL (or the MMY
   * default height when there are no URL params), and keep both copies in sync.
   * @param {HTMLElement} grid
   */
  function initFacets(grid) {
    const root = getCollectionRoot(grid);
    const forms = [...root.querySelectorAll('[data-mmy-facets]')].filter(
      (node) => node instanceof HTMLElement,
    );
    if (forms.length === 0) return;
    if (root.dataset.mmyFacetsInit === '1') return;
    root.dataset.mmyFacetsInit = '1';

    const fromUrl = readStateFromUrl();
    if (!fromUrl) {
      const defaultHeight = readMmyHeightFilter();
      if (defaultHeight) state.height.add(defaultHeight);
    }
    pruneStateToOptions(root);
    reflectStateToInputs(root);
    writeStateToUrl();

    for (const form of forms) {
      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.name.startsWith(FACET_PREFIX)) return;
        if (getFacetAxis(target) === 'height' && target.checked) {
          exclusiveCheckHeight(getFacetInputs(form), target);
        }
        // Read only from the form that changed so a stale twin checkbox can't
        // re-apply a value the user just unchecked in the other copy.
        readStateFromInputs(form);
        reflectStateToInputs(root);
        writeStateToUrl();
        scheduleMmyCollectionFilter();
      });
    }

    for (const clearButton of root.querySelectorAll('[data-mmy-facet-clear]')) {
      if (!(clearButton instanceof HTMLElement)) continue;
      clearButton.addEventListener('click', () => clearAllFacets(root));
    }
  }

  function initMmyCollectionFilter() {
    const grid = getCollectionGrid();
    if (!grid) return;

    cacheOriginalProductsCountTexts(grid);
    initFacets(grid);
    scheduleMmyCollectionFilter();

    const productGrid = grid.querySelector('.product-grid');
    if (!(productGrid instanceof HTMLElement)) return;

    if (observer) observer.disconnect();
    observer = new MutationObserver((mutations) => {
      const hasNewProducts = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          [...mutation.addedNodes].some(
            (node) =>
              node instanceof HTMLElement &&
              (node.matches('li.product-grid__item[data-mmy-height]') ||
                node.querySelector('li.product-grid__item[data-mmy-height]')),
          ),
      );

      if (hasNewProducts) {
        scheduleMmyCollectionFilter();
      }
    });

    observer.observe(productGrid, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMmyCollectionFilter);
  } else {
    initMmyCollectionFilter();
  }

  window.addEventListener('pageshow', scheduleMmyCollectionFilter);
  document.addEventListener('shopify:section:load', initMmyCollectionFilter);
  document.addEventListener('mmy:apply-filter', scheduleMmyCollectionFilter);

  window.applyMmyCollectionFilter = applyMmyCollectionFilter;
  window.readMmyHeightFilter = readMmyHeightFilter;
})();
